type UnitTest = (
  a: any,
  fullObject: any,
  currentKey: string
) => { valid: boolean; format?: any; message?: string };

type Type =
  | "string"
  | "number"
  | "bigint"
  | "boolean"
  | "symbol"
  | "undefined"
  | "function"
  | "array"
  | UnitTest;

export type ObjectSchema<T = any> = Record<
  keyof T,
  Type | { [k: string]: Type }
>;

export type ErrorProps<T> = Partial<Record<keyof T, string>>;

export function objectSchema<T = any>(schema: ObjectSchema<T>, object: any) {
  const fullObject = { ...object };
  const objectCopy = { ...object };
  let fieldsErrors: (keyof T)[] = [];
  const errors: any = {};
  const keys = Object.keys(schema) as (keyof T)[];
  const objectKeys = Object.keys(objectCopy) as (keyof T)[];
  for (let key of objectKeys)
    if (keys.indexOf(key) === -1) delete objectCopy[key];

  for (let key of keys) {
    if (typeof schema[key] !== "object") {
      switch (schema[key]) {
        case "array":
          if (!Array.isArray(objectCopy[key])) fieldsErrors.push(key);
          break;
        default:
          if (typeof schema[key] === "function") {
            const validate = schema[key] as UnitTest;
            const validation = validate(
              objectCopy[key],
              fullObject,
              key as any
            );
            const valid = validation.valid;
            const message = validation.message;
            const format = validation.format;
            if (!valid) {
              fieldsErrors.push(key);
              if (message) errors[key] = message;
            }
            if (Object.keys(validation).includes("format"))
              objectCopy[key] = format;
          } else if (!(typeof objectCopy[key] === schema[key]))
            fieldsErrors.push(key);
      }
    } else {
      const objectTest = objectSchema(
        schema[key] as ObjectSchema,
        objectCopy[key]
      );
      fieldsErrors = fieldsErrors.concat(objectTest.fieldsErrors as any);
      Object.assign(errors, objectTest.errors);
      Object.assign(objectCopy[key], objectTest.body);
    }
  }

  return {
    isValid: !fieldsErrors.length,
    fieldsErrors,
    body: objectCopy,
    errors: errors as ErrorProps<T>,
  };
}

export function isObjectEqual(
  object1: any = {},
  object2: any = {},
  exception?: string[]
) {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);
  if (keys1.length !== keys2.length) {
    return false;
  }
  for (let key of keys1) {
    if (!exception?.includes(key))
      if (Array.isArray(object1[key])) {
        if (!Array.isArray(object2[key])) return false;
        if (object1[key].length !== object2[key].length) return false;
        for (let i = 0; i < object1[key].length; i++) {
          if (object1[key][i] !== object2[key][i]) return false;
        }
      } else if (object1[key] !== object2[key]) return false;
  }
  return true;
}

// TODO: uma funcao que vai pegar esses 2 objetos e saber quais props que estao diferentes
export function isObjectDiff(object1: any, object2: any, exception?: string[]) {
  if (!isObjectEqual(object1, object2)) {
    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);
    const differentKeysList = [];
    // if (keys1.length !== keys2.length) {
    //   return false;
    // }
    for (let key of keys1) {
      if (!exception?.includes(key))
        if (Array.isArray(object1[key])) {
          if (object1[key].length !== object2[key].length) {
            differentKeysList.push(key);
          }
          if (!Array.isArray(object2[key])) {
            differentKeysList.push(key);
          }
          for (let i = 0; i < object1[key].length; i++) {
            if (object1[key][i] !== object2[key][i]) {
              differentKeysList.push(key);
            }
          }
        } else if (object1[key] !== object2[key]) {
          differentKeysList.push(key);
        }
    }
    return { differentKeysList };
  } else return { differentKeysList: [] };
}

export const isObject = (x: any) =>
  typeof x === "object" && !Array.isArray(x) && x !== null;

export function objectToFormData(
  obj: Record<string, any>,
  form?: FormData,
  namespace?: string
): FormData {
  const formData = form || new FormData();

  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (value === undefined || value === null) continue;

    const formKey = namespace ? `${namespace}[${key}]` : key;

    // Se for File ou Blob, adiciona diretamente
    if (value instanceof File || value instanceof Blob) {
      formData.append(formKey, value);
    }
    // Se for FileList, trata como array de arquivos
    else if (typeof FileList !== "undefined" && value instanceof FileList) {
      Array.from(value).forEach((file, i) => {
        formData.append(`${formKey}[${i}]`, file);
      });
      if (value.length === 0) {
        formData.append(formKey, "");
      }
    }
    // Se for array, processa cada item
    else if (Array.isArray(value)) {
      if (value.length === 0) {
        formData.append(formKey, "");
      } else {
        value.forEach((v, i) => {
          const arrayKey = `${formKey}[${i}]`;
          if (v instanceof File || v instanceof Blob) {
            formData.append(arrayKey, v);
          } else if (typeof FileList !== "undefined" && v instanceof FileList) {
            Array.from(v).forEach((file, j) => {
              formData.append(`${arrayKey}[${j}]`, file);
            });
            if (v.length === 0) {
              formData.append(arrayKey, "");
            }
          } else if (typeof v === "object" && v !== null) {
            objectToFormData(v, formData, arrayKey);
          } else {
            formData.append(arrayKey, String(v));
          }
        });
      }
    }
    // Se for objeto simples, faz chamada recursiva
    else if (typeof value === "object") {
      const keys = Object.keys(value);
      if (keys.length === 0) {
        formData.append(formKey, "");
      } else {
        objectToFormData(value, formData, formKey);
      }
    }
    // Caso simples (string, number, boolean)
    else {
      formData.append(formKey, String(value));
    }
  }

  return formData;
}
